/**
 * ForensicTelemetrySnapshotDialog — shows the frame-accurate device_data
 * row that matches an Event Video clip by event_code + recording window.
 *
 * Same visual layout as Analytics → History → Forensic Panel → Telemetry
 * tab (uses the shared <ForensicTelemetryCard /> component) so the data
 * recipients see is identical across the platform.
 *
 * Backend: GET /api/media/event-videos/:id/event-telemetry
 *   ↳ privilege-gated by the existing ``analytics_history_forensic_panel``.
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { Loader2, Cpu, ShieldAlert, Activity } from 'lucide-react';
import { ForensicTelemetryCard } from '../forensic/ForensicTelemetryCard';
import { useAuth } from '../../contexts/AuthContext';
import { resolveTimezone } from '../../utils/timezone';

const sourceBadgeClass = (source) =>
  source === 'event_packet'
    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-300/40'
    : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-300/40';

const sourceLabel = (source) =>
  source === 'event_packet' ? 'Frame-accurate (event packet)' : 'Best-effort (FE79 wrapper)';

const telemetryRowsFor = (data, video) => {
  if (Array.isArray(data?.events) && data.events.length > 0) return data.events;
  if (!data) return [];
  return [{
    event_code: data.gps_match_meta?.matched_event_code || video?.event_type,
    event_label: data.event_label || video?.event_label || video?.event_label_display || 'Event',
    event_label_with_code: data.event_label || video?.event_label_display || video?.event_label || 'Event',
    point: data.point,
    gps_match_meta: data.gps_match_meta,
    gps_source: data.gps_source,
    reason: data.reason,
  }];
};

export const ForensicTelemetrySnapshotDialog = ({ open, onOpenChange, video, device }) => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !video?.id) {
      // Reset on close — correct effect usage that the rule over-flags.

      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`/media/event-videos/${video.id}/event-telemetry`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch((e) => {
        if (cancelled) return;
        if (e?.response?.status === 403) {
          setError(
            "The 'History Forensic Panel' privilege is not enabled for this account.",
          );
        } else if (e?.response?.status === 404) {
          setError('No event-coded telemetry found for this clip.');
        } else {
          setError(e?.response?.data?.detail || e?.message || 'Failed to load telemetry');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, video?.id, api]);

  const tz =
    data?.device_timezone ||
    device?.effective_timezone ||
    resolveTimezone({ device });
  const telemetryRows = telemetryRowsFor(data, video);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="forensic-telemetry-snapshot-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Cpu className="w-5 h-5 text-blue-500" />
            Forensic Telemetry Snapshot
          </DialogTitle>
          <DialogDescription>
            The exact GPS / speed / heading the camera burned into this clip's
            OSD overlay — sourced from the device's own event packet, not
            estimated from nearby samples.
          </DialogDescription>
        </DialogHeader>

        {/* Context strip: event label + match-quality badge */}
        <div className="flex flex-wrap items-center gap-2 pt-1 pb-3">
          {(video?.event_label_display || video?.event_label || video?.event_type) && (
            <Badge variant="outline" className="text-xs">
              {video?.event_label_display || video?.event_label || `Event ${video?.event_type}`}
            </Badge>
          )}
          {video?.has_multiple_events && (
            <Badge
              variant="outline"
              className="text-[11px] bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-300/40"
              data-testid="telemetry-multiple-events-count"
            >
              {telemetryRows.length || video?.event_codes?.length || 0} event packets
            </Badge>
          )}
          {data?.device_name && (
            <span className="text-xs text-muted-foreground" data-testid="forensic-telemetry-device-label">{data.device_name}</span>
          )}
        </div>

        {loading && (
          <div
            className="flex items-center justify-center py-10 text-sm text-muted-foreground"
            data-testid="forensic-snapshot-loading"
          >
            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            Looking up the event-coded packet…
          </div>
        )}

        {!loading && error && (
          <div
            className="py-8 px-4 text-sm flex items-start gap-3 rounded-lg border border-amber-300/40 bg-amber-50/40 dark:bg-amber-950/20"
            data-testid="forensic-snapshot-error"
          >
            <ShieldAlert className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-700 dark:text-amber-300">
                Forensic Snapshot unavailable
              </p>
              <p className="text-muted-foreground mt-1">{error}</p>
            </div>
          </div>
        )}

        {!loading && !error && data && (
          <div className="space-y-4" data-testid="forensic-telemetry-event-list">
            {telemetryRows.length === 0 ? (
              <ForensicTelemetryCard
                point={null}
                timezone={tz}
                ioMappings={data.io_mappings}
                showMotionBadge={true}
                emptyHint="This clip was recorded without an event code, so no frame-accurate packet was emitted by the device."
              />
            ) : telemetryRows.map((row, idx) => (
              <section
                key={`${row.event_code || 'event'}-${idx}`}
                className="rounded-lg border border-slate-200 dark:border-slate-700 p-3 bg-background/60"
                data-testid={`forensic-telemetry-event-section-${idx}`}
              >
                <div className="mb-3 flex flex-wrap items-center gap-2">
                  <Badge variant="outline" className="text-xs" data-testid={`forensic-telemetry-event-label-${idx}`}>
                    {row.event_label_with_code || row.event_label || `Event ${row.event_code || idx + 1}`}
                  </Badge>
                  <Badge
                    variant="outline"
                    className={`text-[11px] ${sourceBadgeClass(row.gps_source)}`}
                    data-testid={`telemetry-source-${row.gps_source}-${idx}`}
                  >
                    <Activity className="w-3 h-3 mr-1" />
                    {sourceLabel(row.gps_source)}
                  </Badge>
                </div>
                <ForensicTelemetryCard
                  point={row.point}
                  timezone={tz}
                  ioMappings={data.io_mappings}
                  showMotionBadge={true}
                  emptyHint={
                    row.reason === 'no_event_code_on_clip'
                      ? "This clip was recorded without an event code, so no frame-accurate packet was emitted by the device."
                      : "No device packet matched this event code inside the recording window. The card values are best-effort from the FE79 wrapper."
                  }
                />
                {row.gps_match_meta?.matched === false && row.point == null && (
                  <div className="mt-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3 text-[11px] text-muted-foreground" data-testid={`forensic-telemetry-audit-details-${idx}`}>
                    <p className="font-semibold mb-1">Audit details</p>
                    <p>
                      Searched <code>device_data</code> for{' '}
                      <code>event_code = {row.gps_match_meta?.expected_event_code ?? row.event_code ?? '—'}</code>{' '}
                      between{' '}
                      <code>{row.gps_match_meta?.window_start || '—'}</code> and{' '}
                      <code>{row.gps_match_meta?.window_end || '—'}</code> — no match.
                    </p>
                  </div>
                )}
              </section>
            ))}
          </div>
        )}

        {data?.device_imei && (
          <div className="pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
            <p className="text-[11px] text-muted-foreground flex items-center gap-1">
              <Cpu className="w-3 h-3" />
              IMEI: <span className="font-mono">{data.device_imei}</span>
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ForensicTelemetrySnapshotDialog;
